/**
 * Ambience recipes (pure data + event planning). Each theme's ambience is a
 * very quiet bed of filtered, slowly swelling noise plus, for some kinds,
 * sparse synthesized events (bird chirps, cricket trains, bubbles, stream
 * plips). Everything plays on the ambience bus, capped at 0.08 of master.
 *
 * DOM-free: event timing and pitches come from a caller-supplied Rng
 * (unit-tested in node); ambiencePlayer.ts turns them into WebAudio nodes.
 */
import type { Rng, ThemeAudio } from '@dropstack/core';
import type { NoiseFilterType } from './sfxSpecs.ts';

export type AmbienceKind = Exclude<ThemeAudio['ambience'], 'none'>;

export interface NoiseBed {
  filter: { type: NoiseFilterType; hz: number; q: number };
  /** Level (0–1) inside the ambience bus. */
  gain: number;
  /** Slow amplitude swell: LFO rate (Hz) and depth (fraction of the level that swells, 0–1). */
  swell?: { hz: number; depth: number };
  /** Slow wander of the filter frequency. */
  sweep?: { hz: number; depthHz: number };
}

/** One short sine blip with a pitch glide (a chirp, a cricket pulse, a bubble). */
export interface AmbienceBlip {
  /** Offset (s) from the event start. */
  at: number;
  hz: number;
  toHz: number;
  dur: number;
  /** Peak level (0–1). */
  gain: number;
}

export interface AmbienceEvent {
  blips: AmbienceBlip[];
  /** Stereo position −1…1. */
  pan: number;
}

export const AMBIENCE_BEDS: Readonly<Record<AmbienceKind, readonly NoiseBed[]>> = {
  // A faint leafy breeze under the birdsong.
  birds: [{ filter: { type: 'lowpass', hz: 1200, q: 0.5 }, gain: 0.12, swell: { hz: 0.07, depth: 0.5 } }],
  // Surf: a deep swell with a little hiss riding on the same rhythm.
  waves: [
    { filter: { type: 'lowpass', hz: 650, q: 0.4 }, gain: 0.6, swell: { hz: 0.085, depth: 0.85 } },
    { filter: { type: 'highpass', hz: 2500, q: 0.3 }, gain: 0.12, swell: { hz: 0.085, depth: 0.9 } },
  ],
  wind: [
    { filter: { type: 'bandpass', hz: 480, q: 0.8 }, gain: 0.5, swell: { hz: 0.06, depth: 0.6 }, sweep: { hz: 0.045, depthHz: 220 } },
    { filter: { type: 'bandpass', hz: 1400, q: 1.5 }, gain: 0.12, swell: { hz: 0.11, depth: 0.7 } },
  ],
  stream: [
    { filter: { type: 'bandpass', hz: 1800, q: 0.8 }, gain: 0.35, swell: { hz: 0.7, depth: 0.25 } },
    { filter: { type: 'highpass', hz: 4000, q: 0.3 }, gain: 0.12, swell: { hz: 1.3, depth: 0.3 } },
  ],
  insects: [{ filter: { type: 'highpass', hz: 5000, q: 0.5 }, gain: 0.05, swell: { hz: 0.2, depth: 0.4 } }],
  // A soft underwater rumble.
  bubbles: [{ filter: { type: 'lowpass', hz: 400, q: 0.7 }, gain: 0.18, swell: { hz: 0.1, depth: 0.4 } }],
};

/** Seconds between events (uniform in [min, max]); kinds without events are absent. */
export const AMBIENCE_EVENT_GAPS: Readonly<Partial<Record<AmbienceKind, readonly [number, number]>>> = {
  birds: [2.5, 7],
  insects: [1.2, 3.5],
  bubbles: [0.8, 3],
  stream: [0.3, 1.1],
};

const between = (rng: Rng, lo: number, hi: number): number => lo + rng.next() * (hi - lo);

function birdCall(rng: Rng): AmbienceEvent {
  const blips: AmbienceBlip[] = [];
  const count = rng.int(2, 5);
  let hz = between(rng, 2200, 3800);
  let at = 0;
  for (let i = 0; i < count; i++) {
    const dur = between(rng, 0.05, 0.12);
    blips.push({ at, hz, toHz: hz * between(rng, 0.75, 1.35), dur, gain: between(rng, 0.35, 0.6) });
    at += dur + between(rng, 0.08, 0.18);
    hz *= between(rng, 0.92, 1.08);
  }
  return { blips, pan: between(rng, -0.6, 0.6) };
}

function cricketTrain(rng: Rng): AmbienceEvent {
  const pulses = rng.int(6, 14);
  const rate = between(rng, 14, 22);
  const hz = between(rng, 4200, 5200);
  const gain = between(rng, 0.12, 0.22);
  const blips: AmbienceBlip[] = [];
  for (let i = 0; i < pulses; i++) blips.push({ at: i / rate, hz, toHz: hz * 0.98, dur: 0.018, gain });
  return { blips, pan: between(rng, -0.7, 0.7) };
}

function bubbleCluster(rng: Rng): AmbienceEvent {
  const blips: AmbienceBlip[] = [];
  const count = rng.int(1, 4);
  let at = 0;
  for (let i = 0; i < count; i++) {
    const hz = between(rng, 280, 650);
    blips.push({ at, hz, toHz: hz * between(rng, 1.8, 2.6), dur: between(rng, 0.05, 0.09), gain: between(rng, 0.25, 0.45) });
    at += between(rng, 0.05, 0.2);
  }
  return { blips, pan: between(rng, -0.5, 0.5) };
}

function streamPlip(rng: Rng): AmbienceEvent {
  const hz = between(rng, 900, 1600);
  return {
    blips: [{ at: 0, hz, toHz: hz * between(rng, 1.3, 1.6), dur: between(rng, 0.03, 0.05), gain: between(rng, 0.1, 0.2) }],
    pan: between(rng, -0.6, 0.6),
  };
}

const EVENT_MAKERS: Readonly<Partial<Record<AmbienceKind, (rng: Rng) => AmbienceEvent>>> = {
  birds: birdCall,
  insects: cricketTrain,
  bubbles: bubbleCluster,
  stream: streamPlip,
};

/**
 * Plan the next ambience event of `kind`: the gap (s) to wait before it and
 * its blips. Null for kinds that are beds only.
 */
export function planAmbienceEvent(kind: AmbienceKind, rng: Rng): { gapS: number; event: AmbienceEvent } | null {
  const gaps = AMBIENCE_EVENT_GAPS[kind];
  const make = EVENT_MAKERS[kind];
  if (!gaps || !make) return null;
  const gapS = between(rng, gaps[0], gaps[1]);
  return { gapS, event: make(rng) };
}

/** Seconds from event start until its last blip has finished. */
export function ambienceEventDurationS(e: AmbienceEvent): number {
  return e.blips.reduce((end, b) => Math.max(end, b.at + b.dur), 0);
}
